import React, { useState } from "react";
import { Info } from "lucide-react";
import { useSkillLevelNamingOptional } from "../../contexts/SkillLevelNamingContext";
import SkillLevelNamingTableModal from "./SkillLevelNamingTableModal";
import { resolveSkillLevelDisplayLabel, inferSkillIdFromAnyLabel } from "@/constants/skillLevelStateNaming";

type Props = {
  skillId?: string | null;
  label?: string | null;
  minimum?: boolean;
  className?: string;
  infoClassName?: string;
  showInfo?: boolean;
  /** Show only the (i) button — for field labels without a value yet. */
  infoOnly?: boolean;
};

/**
 * Renders a skill level label with an (i) button that opens the cross-state table.
 */
export default function SkillLevelWithInfo({
  skillId,
  label,
  minimum = true,
  className = "",
  infoClassName = "",
  showInfo = true,
  infoOnly = false,
}: Props) {
  const naming = useSkillLevelNamingOptional();
  const [open, setOpen] = useState(false);

  const resolved = resolveSkillLevelDisplayLabel({
    skillId: skillId || inferSkillIdFromAnyLabel(label),
    canonicalName: label,
    namingState: naming?.namingState || "DEFAULT",
    minimum,
  });

  if (!resolved && !infoOnly) return null;

  return (
    <>
      <span className={`inline-flex items-center gap-1.5 ${className}`}>
        {!infoOnly && resolved ? <span>{resolved}</span> : null}
        {showInfo && (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setOpen(true);
            }}
            className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:border-indigo-300 hover:text-indigo-600 ${infoClassName}`}
            aria-label="View skill level naming by state"
            title="Skill level naming by state"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
      <SkillLevelNamingTableModal
        open={open}
        onClose={() => setOpen(false)}
        rows={naming?.referenceRows}
        highlightState={naming?.namingState || "DEFAULT"}
      />
    </>
  );
}
